import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loopsConfig, workflows, evaluators, chat } from './loops';
import { detectClaude, fetchFileRaw, checkServerStatus } from '../../utils/api';
import { buildDefaultStepsWithSkills } from './loops/workflows/workflowHelpers';
import { ASSET_BASE_URL, API_BASE_URL } from '../../config/constants';
import OnboardingChecklist from '../../components/OnboardingChecklist';
import NewFileTree from '../../components/NewFileTree';
import FileList from '../../components/FileList';
import SignInModal from '../../components/SignInModal';
const { WorkflowList, WorkflowEditor } = workflows;
const { EvaluationList } = evaluators;
const { ExecutionList } = chat;

const SAVED_WORKFLOWS_KEY = 'bonzai-saved-workflows';

function getSavedWorkflows() {
  try {
    return JSON.parse(localStorage.getItem(SAVED_WORKFLOWS_KEY) || '[]');
  } catch { return []; }
}

function saveWorkflowSnapshot(name, steps, timestamp) {
  const saved = getSavedWorkflows();
  saved.push({
    id: `wf-${Date.now()}`,
    name,
    timestamp: timestamp || new Date().toISOString(),
    steps: JSON.parse(JSON.stringify(steps)),
  });
  localStorage.setItem(SAVED_WORKFLOWS_KEY, JSON.stringify(saved));
}

function Canvas() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('workflows');

  // Determine active loops: use URL param if present, otherwise use config
  const activeLoops = useMemo(() => {
    const loopsParam = searchParams.get('loops');
    if (loopsParam) {
      // URL param format: ?loops=territory,workflows,evaluators,chat
      const requestedLoops = loopsParam.split(',').map(l => l.trim().toLowerCase());
      return {
        territory: requestedLoops.includes('territory'),
        workflows: requestedLoops.includes('workflows'),
        evaluators: requestedLoops.includes('evaluators'),
        chat: requestedLoops.includes('chat'),
      };
    }
    // Fall back to config file
    return loopsConfig;
  }, [searchParams]);
  // Two top-level bools: editor goes straight to WorkflowEditor, agentMenu shows config panels
  const [showEditor, setShowEditor] = useState(true);
  const [showAgentMenu, setShowAgentMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Default open
  const [showSignInModal, setShowSignInModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('lastUpdated');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [highlightedFileIds, setHighlightedFileIds] = useState([]);
  const [workflowListKey, setWorkflowListKey] = useState(0); // Force refresh on save
  const [detectedSkills, setDetectedSkills] = useState(null); // null = loading, [] = none
  const [serverStatus, setServerStatus] = useState('checking'); // 'checking' | 'active' | 'inactive'
  const [showOnboarding, setShowOnboarding] = useState(() => {
    // First time only: check localStorage
    const dismissed = localStorage.getItem('bonzai_onboarding_dismissed');
    return !dismissed;
  });
  const [hasWrittenToRepo, setHasWrittenToRepo] = useState(false);
  const [hasAddedSkill, setHasAddedSkill] = useState(false);
  const [onboardingWorkflowOverride, setOnboardingWorkflowOverride] = useState(null);
  const hasSavedPrebonzai = useRef(false);
  const [workflowResetKey, setWorkflowResetKey] = useState(0);

  // Shared ref so header can trigger content's write handler
  const writeToRepoHandlerRef = useRef(null);

  // Poll server health
  useEffect(() => {
    const check = async () => {
      const ok = await checkServerStatus();
      setServerStatus(ok ? 'active' : 'inactive');
    };
    check();
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (serverStatus !== 'active') return;
    detectClaude()
      .then(async data => {
        const rawSkills = data.skills || [];

        // Read content for each skill file via /read endpoint
        const skills = await Promise.all(
          rawSkills.map(async (skill) => {
            const skillPath = typeof skill === 'string' ? skill : skill.path;
            try {
              const content = await fetchFileRaw(skillPath);
              return { path: skillPath, content };
            } catch (err) {
              console.error(`[Canvas] failed to read ${skillPath}:`, err.message);
              return { path: skillPath, content: '' };
            }
          })
        );

        setDetectedSkills(skills);
      })
      .catch(err => {
        console.error('[Canvas] detect-claude failed:', err);
        setDetectedSkills([]);
      });
  }, [serverStatus]);

  // Save prebonzai workflow when onboarding starts and there are existing skills
  useEffect(() => {
    if (showOnboarding && detectedSkills && detectedSkills.length > 0 && !hasSavedPrebonzai.current) {
      hasSavedPrebonzai.current = true;

      // Build steps from detected skills
      const prebonzaiSteps = buildDefaultStepsWithSkills(detectedSkills);

      // Check if a prebonzai workflow already exists
      const existingWorkflows = getSavedWorkflows();
      const prebonzaiExists = existingWorkflows.some(w => w.name === 'Pre-Bonzai Configuration');

      // Only save if it doesn't already exist
      if (!prebonzaiExists) {
        saveWorkflowSnapshot('Pre-Bonzai Configuration', prebonzaiSteps, new Date().toISOString());
      }

      // Set empty workflow for onboarding and force reset
      setOnboardingWorkflowOverride(buildDefaultStepsWithSkills([]));
      setWorkflowResetKey(prev => prev + 1); // Force remount to show empty workflow
    }
  }, [showOnboarding, detectedSkills]);


  const handleFileSelect = () => {
    // File selection handler
  };

  const handleDisconnect = async () => {
    try {
      await fetch(`${API_BASE_URL}/shutdown`, { method: 'POST', mode: 'cors' });
    } catch (e) {}
    setTimeout(() => { window.location.href = 'https://bonzai.dev'; }, 1000);
  };

  const handleStepHover = (fileIds) => {
    setHighlightedFileIds(fileIds);
  };

  const handleWorkflowClick = (workflow) => {
    setSelectedWorkflow(workflow);
  };

  const handleBackToList = () => {
    setSelectedWorkflow(null);
  };

  const handleCreateWorkflow = () => {
    const newWorkflow = {
      id: `workflow-${Date.now()}`,
      name: 'Select Workflow',
      description: '',
      lastUpdated: 'Just now',
      created: new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long' }),
      tags: [],
      inChat: false,
      currentVersion: 'v1',
      steps: buildDefaultStepsWithSkills(detectedSkills || []),
      evaluation: {
        currentScore: 0,
        trend: 'stable',
        totalLifetimePushes: 0,
        versions: [
          {
            id: 'v1',
            number: 'v1',
            status: 'active',
            score: 0,
            previousScore: null,
            pushes: 0,
            cleanPushes: 0,
            revertedPushes: 0,
            pushHistory: []
          }
        ]
      }
    };
    setSelectedWorkflow(newWorkflow);
  };

  const handleDismissOnboarding = () => {
    localStorage.setItem('bonzai_onboarding_dismissed', 'true');
    setShowOnboarding(false);
    setOnboardingWorkflowOverride(null);
  };

  const handleToggleOnboarding = () => {
    // Full reset to simulate first-time experience
    localStorage.removeItem('bonzai_onboarding_dismissed');

    // Reset hint flags so they show again
    localStorage.removeItem('bonzai-terminal-hint-clicked');
    localStorage.removeItem('bonzai-performance-hint-clicked');
    localStorage.removeItem('bonzai-workflow-switch-hint-clicked');

    setShowOnboarding(true);
    setHasAddedSkill(false);
    setHasWrittenToRepo(false);
    setOnboardingWorkflowOverride(buildDefaultStepsWithSkills([]));
    setWorkflowResetKey(prev => prev + 1); // Force remount to show empty workflow
    hasSavedPrebonzai.current = false;
  };

  // Build steps from detected skills (memoize so step IDs are stable)
  const detectedSteps = useMemo(() => {
    if (!detectedSkills) return null;
    return buildDefaultStepsWithSkills(detectedSkills);
  }, [detectedSkills]);

  // Determine the workflow to show in the editor
  const editorWorkflow = selectedWorkflow || (showEditor ? {
    id: 'default-workflow',
    name: 'Select Workflow',
    description: '',
    lastUpdated: 'Just now',
    created: new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long' }),
    tags: [],
    inChat: false,
    currentVersion: 'v1',
    // Use onboarding override if in onboarding mode, otherwise use detected steps
    steps: showOnboarding && onboardingWorkflowOverride ? onboardingWorkflowOverride : (detectedSteps || undefined),
  } : null);

  // Server not running — show prompt to start it
  if (serverStatus !== 'active') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
        padding: '40px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif'
      }}>
        <a href="https://bonzai.dev" style={{
          position: 'absolute',
          top: '24px',
          left: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          textDecoration: 'none'
        }}>
          <img
            src={`${ASSET_BASE_URL}/favicon.jpg`}
            alt="Bonzai"
            style={{ width: '32px', height: '32px', borderRadius: '6px' }}
          />
          <span style={{ fontSize: '20px', fontWeight: '600', color: '#333' }}>bonzai</span>
        </a>

        <div style={{
          background: '#fff',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#22c55e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white" style={{ width: '28px', height: '28px' }}>
              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
            </svg>
          </div>

          <h2 style={{
            margin: '0 0 12px',
            fontSize: '24px',
            fontWeight: '600',
            color: '#333'
          }}>
            Almost there!
          </h2>

          <p style={{
            margin: '0 0 16px',
            fontSize: '15px',
            color: '#666',
            lineHeight: '1.6'
          }}>
            Your workflow editor will appear here.
          </p>

          <p style={{
            margin: '0',
            fontSize: '15px',
            color: '#666',
            lineHeight: '1.6'
          }}>
            To start, run{' '}
            <code style={{
              background: '#f3f4f6',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '13px',
              whiteSpace: 'nowrap'
            }}>
              npx bonzai-tree -v
            </code>{' '}
            in a local repo.
          </p>
        </div>

        {/* Sign In Modal */}
        <SignInModal
          isOpen={showSignInModal}
          onClose={() => setShowSignInModal(false)}
        />
      </div>
    );
  }

  // Show workflow editor if a workflow is selected OR showEditor is true
  if (editorWorkflow) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
        background: '#ffffff',
        position: 'relative'
      }}>
        {/* Onboarding Checklist */}
        {showOnboarding && (
          <OnboardingChecklist
            serverStatus={serverStatus}
            hasSkills={hasAddedSkill}
            hasWritten={hasWrittenToRepo}
            onDismiss={handleDismissOnboarding}
          />
        )}

        {/* Help Button & Feedback - Bottom Right (only in dev) */}
        {process.env.REACT_APP_ENV === 'local' && (
          <div style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            zIndex: 1000
          }}>
            {/* Feedback Button */}
            <a
              href="mailto:admin@bonzai.dev"
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                background: '#fff',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: '#6b7280',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              Got feedback?
            </a>

            {/* Help Button */}
            <button
              onClick={handleToggleOnboarding}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                background: '#fff',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 600,
                color: '#6b7280',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title="Toggle getting started guide"
            >
              ?
            </button>
          </div>
        )}

        {/* Workflows Loop - Main Content (Header + Editor with file browser) */}
        {activeLoops.workflows && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: showOnboarding ? '1fr' : (sidebarCollapsed ? '48px 1fr' : '280px 1fr'),
            gridTemplateRows: 'auto 1fr',
            flex: 1,
            overflow: 'hidden',
            transition: 'grid-template-columns 0.2s ease'
          }}>
            {/* Header spans all columns */}
            <div style={{ gridColumn: showOnboarding ? '1' : '1 / 3', gridRow: '1', position: 'relative' }}>
              <WorkflowEditor
                key={workflowResetKey}
                workflow={editorWorkflow}
                onBack={handleBackToList}
                onStepHover={handleStepHover}
                onSave={() => {
                  setSelectedWorkflow(null);
                  setWorkflowListKey(prev => prev + 1);
                  setHasWrittenToRepo(true);
                }}
                onSkillAdded={() => setHasAddedSkill(true)}
                showOnboarding={showOnboarding}
                canShowWriteButton={!showOnboarding || (serverStatus === 'active' && hasAddedSkill)}
                activeLoops={activeLoops}
                showAgentMenu={showAgentMenu}
                onToggleAgentMenu={() => setShowAgentMenu(prev => !prev)}
                showEditorToggle={showEditor}
                onToggleEditor={() => setShowEditor(prev => !prev)}
                hideContentBelowHeader={true}
                onWriteToRepo={() => {
                  // Call the content instance's handler
                  if (writeToRepoHandlerRef.current) {
                    writeToRepoHandlerRef.current();
                  }
                }}
                onToggleFileSystem={() => setSidebarCollapsed(prev => !prev)}
                fileSystemOpen={!sidebarCollapsed}
                onOpenSignInModal={() => setShowSignInModal(true)}
              />
            </div>

            {/* File Browser - Left column, below header */}
            {!showOnboarding && (
            <div style={{
              gridColumn: '1',
              gridRow: '2',
              borderRight: '1px solid #e5e5e5',
              background: '#fafafa',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              paddingTop: '61px'
            }}>
              {sidebarCollapsed ? (
                // Collapsed view - just chevron and power button
                <>
                  <div style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #e5e5e5',
                    background: '#fafafa',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <button
                      onClick={() => setSidebarCollapsed(false)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Expand sidebar"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '20px', height: '20px' }}>
                        <path fillRule="evenodd" d="M15.79 14.77a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L11.832 10l3.938 3.71a.75.75 0 01.02 1.06zm-6 0a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L5.832 10l3.938 3.71a.75.75 0 01.02 1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  <div style={{ flex: 1 }}></div>
                  <div style={{
                    padding: '12px 0',
                    borderTop: '1px solid #e5e5e5',
                    background: '#fafafa',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <button
                      onClick={handleDisconnect}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '8px',
                        color: '#ef4444',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '6px',
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      title="Disconnect"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '20px', height: '20px' }}>
                        <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 0110 3zm-5.5 5a.75.75 0 011.5 0 4 4 0 107.5 2.5.75.75 0 011.5 0A5.5 5.5 0 114.5 8z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </>
              ) : (
                // Expanded view - full content
                <>
                  <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e5e5e5',
                    background: '#fafafa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      flex: 1
                    }}>
                      <div style={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: '#111827'
                      }}>
                        Local Repo Files
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        fontWeight: '400'
                      }}>
                        Files on your computer, with Claude files shown in the canvas.
                      </div>
                    </div>
                    <button
                      onClick={() => setSidebarCollapsed(true)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Collapse sidebar"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                        <path fillRule="evenodd" d="M4.72 9.47a.75.75 0 010 1.06l4.25 4.5a.75.75 0 001.08-1.04L6.31 10l3.74-3.97a.75.75 0 10-1.08-1.04l-4.25 4.5zm6.5 0a.75.75 0 010 1.06l4.25 4.5a.75.75 0 001.08-1.04L13.31 10l3.74-3.97a.75.75 0 00-1.08-1.04l-4.25 4.5z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <NewFileTree onFileClick={handleFileSelect} />
                  </div>
                  {/* Footer with Disconnect button */}
                  <div style={{
                    padding: '12px 16px',
                    borderTop: '1px solid #e5e5e5',
                    background: '#fafafa'
                  }}>
                    <button
                      onClick={handleDisconnect}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#ef4444',
                        cursor: 'pointer',
                        transition: 'background 0.15s ease',
                        width: '100%',
                        justifyContent: 'flex-start'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                        <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 0110 3zm-5.5 5a.75.75 0 011.5 0 4 4 0 107.5 2.5.75.75 0 011.5 0A5.5 5.5 0 114.5 8z" clipRule="evenodd" />
                      </svg>
                      Disconnect
                    </button>
                  </div>
                </>
              )}
            </div>
            )}

            {/* Editor content - Right column, below header */}
            <div style={{
              gridColumn: showOnboarding ? '1' : '2',
              gridRow: '2',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <WorkflowEditor
                key={`${workflowResetKey}-content`}
                workflow={editorWorkflow}
                onBack={handleBackToList}
                onStepHover={handleStepHover}
                onSave={() => {
                  setSelectedWorkflow(null);
                  setWorkflowListKey(prev => prev + 1);
                  setHasWrittenToRepo(true);
                }}
                onSkillAdded={() => setHasAddedSkill(true)}
                showOnboarding={showOnboarding}
                canShowWriteButton={!showOnboarding || (serverStatus === 'active' && hasAddedSkill)}
                activeLoops={activeLoops}
                showAgentMenu={showAgentMenu}
                onToggleAgentMenu={() => setShowAgentMenu(prev => !prev)}
                showEditorToggle={showEditor}
                onToggleEditor={() => setShowEditor(prev => !prev)}
                hideHeader={true}
                onToggleFileSystem={() => setSidebarCollapsed(prev => !prev)}
                fileSystemOpen={!sidebarCollapsed}
                onOpenSignInModal={() => setShowSignInModal(true)}
                writeToRepoHandlerRef={writeToRepoHandlerRef}
              />
            </div>
          </div>
        )}

        {/* Sign In Modal */}
        <SignInModal
          isOpen={showSignInModal}
          onClose={() => setShowSignInModal(false)}
        />
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      width: '100%',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
      background: '#ffffff'
    }}>
      {/* Main Content - Show if any content loop is enabled */}
      {(activeLoops.workflows || activeLoops.evaluators || activeLoops.chat) && (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: '20px'
          }}>
            <div>
              <h1 style={{
                fontSize: '24px',
                fontWeight: 600,
                color: '#111827',
                margin: 0,
                marginBottom: '4px'
              }}>
                Personal
              </h1>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: 0
              }}>
                Workflows, credentials and data tables owned by you
              </p>
            </div>
            {activeLoops.workflows && (
            <button
              onClick={handleCreateWorkflow}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background 0.15s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#ef4444'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
              </svg>
              Create workflow
            </button>
            )}
          </div>

          {/* Tabs - Only show tabs for enabled loops */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            borderBottom: '1px solid #e5e7eb',
            marginLeft: '-32px',
            marginRight: '-32px',
            paddingLeft: '32px',
            paddingRight: '32px'
          }}>
            {activeLoops.workflows && (
            <button
              onClick={() => setActiveTab('workflows')}
              style={{
                padding: '12px 0',
                background: 'transparent',
                border: 'none',
                fontSize: '14px',
                fontWeight: 500,
                color: activeTab === 'workflows' ? '#ef4444' : '#6b7280',
                cursor: 'pointer',
                borderBottom: activeTab === 'workflows' ? '2px solid #ef4444' : '2px solid transparent',
                marginBottom: '-1px',
                transition: 'color 0.15s ease'
              }}
            >
              Constrain
            </button>
            )}
            {activeLoops.chat && (
            <button
              onClick={() => setActiveTab('chats')}
              style={{
                padding: '12px 0',
                background: 'transparent',
                border: 'none',
                fontSize: '14px',
                fontWeight: 500,
                color: activeTab === 'chats' ? '#ef4444' : '#6b7280',
                cursor: 'pointer',
                borderBottom: activeTab === 'chats' ? '2px solid #ef4444' : '2px solid transparent',
                marginBottom: '-1px',
                transition: 'color 0.15s ease'
              }}
            >
              Execute
            </button>
            )}
            {activeLoops.evaluators && (
            <button
              onClick={() => setActiveTab('evaluations')}
              style={{
                padding: '12px 0',
                background: 'transparent',
                border: 'none',
                fontSize: '14px',
                fontWeight: 500,
                color: activeTab === 'evaluations' ? '#ef4444' : '#6b7280',
                cursor: 'pointer',
                borderBottom: activeTab === 'evaluations' ? '2px solid #ef4444' : '2px solid transparent',
                marginBottom: '-1px',
                transition: 'color 0.15s ease'
              }}
            >
              Evaluate
            </button>
            )}
          </div>
        </div>

        {/* Search and Filters - hide for Chats tab */}
        {activeTab !== 'chats' && (
        <div style={{
          padding: '16px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '12px',
          borderBottom: '1px solid #f3f4f6'
        }}>
          <div style={{
            position: 'relative',
            flex: 1,
            maxWidth: '300px'
          }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="#9ca3af"
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '16px',
                height: '16px'
              }}
            >
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
            </svg>
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.15s ease'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            />
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '8px 32px 8px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#374151',
              background: '#fff',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z' clip-rule='evenodd'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px center',
              backgroundSize: '16px'
            }}
          >
            <option value="lastUpdated">Sort by last updated</option>
            <option value="name">Sort by name</option>
            <option value="created">Sort by created</option>
          </select>

          <button style={{
            padding: '8px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            background: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#6b7280" style={{ width: '18px', height: '18px' }}>
              <path d="M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 0 1 .628.74v2.288a2.25 2.25 0 0 1-.659 1.59l-4.682 4.683a2.25 2.25 0 0 0-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 0 1 8 18.25v-5.757a2.25 2.25 0 0 0-.659-1.591L2.659 6.22A2.25 2.25 0 0 1 2 4.629V2.34a.75.75 0 0 1 .628-.74Z" />
            </svg>
          </button>

          <button style={{
            padding: '8px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            background: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#6b7280" style={{ width: '18px', height: '18px' }}>
              <path fillRule="evenodd" d="M4.25 2A2.25 2.25 0 0 0 2 4.25v2.5A2.25 2.25 0 0 0 4.25 9h2.5A2.25 2.25 0 0 0 9 6.75v-2.5A2.25 2.25 0 0 0 6.75 2h-2.5Zm0 9A2.25 2.25 0 0 0 2 13.25v2.5A2.25 2.25 0 0 0 4.25 18h2.5A2.25 2.25 0 0 0 9 15.75v-2.5A2.25 2.25 0 0 0 6.75 11h-2.5Zm9-9A2.25 2.25 0 0 0 11 4.25v2.5A2.25 2.25 0 0 0 13.25 9h2.5A2.25 2.25 0 0 0 18 6.75v-2.5A2.25 2.25 0 0 0 15.75 2h-2.5Zm0 9A2.25 2.25 0 0 0 11 13.25v2.5A2.25 2.25 0 0 0 13.25 18h2.5A2.25 2.25 0 0 0 18 15.75v-2.5A2.25 2.25 0 0 0 15.75 11h-2.5Z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        )}

        {/* Content - Render based on active tab and loop toggles */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* File Browser - Left sidebar */}
          <div style={{ width: '280px', height: '100%', flexShrink: 0 }}>
            <FileList
              onFileClick={handleFileSelect}
              onFolderClick={() => {}}
              onTreeUpdate={() => {}}
              refreshTrigger={0}
            />
          </div>

          {/* Content area */}
          <div style={{ flex: 1, overflow: 'auto', display: 'flex' }}>
            {/* File Browser on left */}
            <div style={{
              width: '280px',
              flexShrink: 0,
              borderRight: '1px solid #e5e5e5',
              background: '#ffffff',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                padding: '12px 16px',
                background: '#ffffff',
                borderBottom: '1px solid #e5e5e5',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px'
              }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#1e1e1e'
                }}>
                  Your File System
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  fontWeight: '400'
                }}>
                  all code stays on your machine
                </div>
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <NewFileTree onFileClick={handleFileSelect} />
              </div>
            </div>

            {/* List content */}
            <div style={{ flex: 1, overflow: 'auto' }}>
              {activeTab === 'workflows' && activeLoops.workflows && (
                <WorkflowList key={workflowListKey} searchQuery={searchQuery} onWorkflowClick={handleWorkflowClick} />
              )}
              {activeTab === 'chats' && activeLoops.chat && (
                <ExecutionList searchQuery={searchQuery} onFileHighlight={handleStepHover} />
              )}
              {activeTab === 'evaluations' && activeLoops.evaluators && (
                <EvaluationList searchQuery={searchQuery} />
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Sign In Modal */}
      <SignInModal
        isOpen={showSignInModal}
        onClose={() => setShowSignInModal(false)}
      />
    </div>
  );
}

export default Canvas;
